"use client";

import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCube, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-cube";
import { Img, Video } from "@/components/ui";

export type Slide = {
  img: string;
  cost?: string;
  darkText?: boolean;
  title: string;
  desc: string;
  reviews?: string;
  stars?: number;
};

export type CubeSliderProps = {
  slides: Slide[];
};

const CubeSlider: React.FC<CubeSliderProps> = ({ slides }) => {
  const isVideo = (src: string) =>
    src.endsWith(".mp4") || src.endsWith(".webm") || src.endsWith(".ogg");

  return (
    <div className="relative mx-auto aspect-[4/5] w-full min-w-0 max-w-[min(100%,17.5rem)] xs:max-w-[19rem] sm:max-w-[21rem] md:max-w-[22rem] lg:max-w-[24rem]">
      <Swiper
        modules={[EffectCube, Autoplay]}
        effect="cube"
        grabCursor
        loop
        speed={1000}
        autoplay={{ delay: 3000, pauseOnMouseEnter: true }}
        cubeEffect={{ shadow: false, slideShadows: true, shadowOffset: 10, shadowScale: 0.94 }}
        observer
        observeParents
        touchEventsTarget="container"
        className="h-full w-full rounded-2xl"
      >
        {slides.map((slide, idx) => (
          <SwiperSlide key={idx} className="relative overflow-hidden rounded-2xl cursor-grab">
            {/* Media */}
            {isVideo(slide.img) ? (
              <Video
                src={slide.img}
                title={slide.title}
                className="absolute inset-0 h-full w-full object-cover rounded-2xl pointer-events-auto"
                autoPlay
                loop
                muted
                playsInline
              />
            ) : (
              <Img
                src={slide.img}
                alt={slide.title}
                title={slide.title}
                width={200}
                height={200}
                className="absolute inset-0 h-full w-full object-cover rounded-2xl pointer-events-auto"
              />
            )}

            {/* Cost Badge */}
            {slide.cost && (
              <div
                className={`absolute top-2 right-2 rounded-full px-3 py-1 text-sm font-semibold backdrop-blur-md shadow-md pointer-events-auto ${
                  slide.darkText ? "text-gray-900 bg-white/60" : "text-white bg-white/30"
                }`}
              >
                {slide.cost}
              </div>
            )}

            {/* Overlay Info */}
            <div className="absolute bottom-0 left-0 w-full rounded-b-2xl border-t border-white/30 bg-black/20 backdrop-blur-xl p-4 text-white pointer-events-none">
              <h2 className="text-lg font-semibold">{slide.title}</h2>
              <p className="text-sm text-white/80">{slide.desc}</p>
              {slide.stars && (
                <div className="mt-1 flex items-center gap-2 pointer-events-none">
                  <div className="flex text-yellow-400">
                    {Array.from({ length: slide.stars }).map((_, i) => (
                      <span key={i}>★</span>
                    ))}
                  </div>
                  <span className="text-xs text-white/80">{slide.reviews}</span>
                </div>
              )}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default CubeSlider;
